import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";

// Import components (lazy-loaded for performance)
const CompanyKnowledgeList = React.lazy(() => import("./CompanyKnowledgeList"));
const CompanyKnowledgeEditor = React.lazy(() => import("./CompanyKnowledgeEditor"));
const CompanyKnowledgeViewer = React.lazy(() => import("./CompanyKnowledgeViewer"));
const CompanyKnowledgeAdmin = React.lazy(() => import("./CompanyKnowledgeAdmin"));
const CompanyKnowledgeWhatsNew = React.lazy(() => import("./CompanyKnowledgeWhatsNew"));
const PublicArticleViewer = React.lazy(() => import("./PublicArticleViewer"));
const SharedArticleViewer = React.lazy(() => import("./SharedArticleViewer"));

const companyKnowledgeModuleConfig: ModuleConfig = {
    // 🏷️ MODULE IDENTITY
    id: "companyknowledge",
    name: "Company Knowledge",
    description: "Internal knowledge base for company processes and training",
    version: "0.0.8",
    enabled: true,
    category: "business",
    order: 16,
    icon: "📖",
    color: "#8B5CF6",

    // 🛣️ ROUTES
    routes: [
        // Private Routes (Authentication required - any logged-in user can view)
        {
            path: "/companyknowledge",
            component: CompanyKnowledgeList,
            permissionLevel: "view",
            useLayout: true
        },
        {
            path: "/companyknowledge/new",
            component: CompanyKnowledgeEditor,
            permissionLevel: "create",
            useLayout: true
        },
        {
            path: "/companyknowledge/:id/edit",
            component: CompanyKnowledgeEditor,
            permissionLevel: "edit",
            useLayout: true
        },
        {
            path: "/companyknowledge/:id",
            component: CompanyKnowledgeViewer,
            permissions: [], // No route-level auth check - component handles visibility-based access
            useLayout: true
        },
        {
            path: "/ck/:id",
            component: CompanyKnowledgeViewer,
            permissions: [], // Short URL alias - same as /companyknowledge/:id
            useLayout: true
        },
        {
            path: "/companyknowledge/admin",
            component: CompanyKnowledgeAdmin,
            permissionLevel: "delete", // Admin-level access
            useLayout: true
        },
        {
            path: "/companyknowledge/whats-new",
            component: CompanyKnowledgeWhatsNew,
            permissionLevel: "delete", // Admin-level access
            useLayout: true
        },
        // Shared Link Route (Authentication only, no permissions)
        {
            path: "/kb/shared/:token",
            component: SharedArticleViewer,
            permissions: [], // Public route but component handles auth
            useLayout: true
        },
        // Completely Public Route (No authentication)
        {
            path: "/kb/:slug",
            component: PublicArticleViewer,
            permissions: [], // Completely public
            useLayout: true
        }
    ],

    // 🧭 NAVIGATION
    navigation: [
        {
            label: "All Articles",
            path: "/companyknowledge",
            icon: "📚",
            permissionLevel: "view"
        },
        {
            label: "New Article",
            path: "/companyknowledge/new",
            icon: "➕",
            permissionLevel: "create"
        },
        {
            label: "Admin Dashboard",
            path: "/companyknowledge/admin",
            icon: "⚙️",
            permissionLevel: "delete" // Admin-level access
        },
        {
            label: "What's New",
            path: "/companyknowledge/whats-new",
            icon: "🎉",
            permissionLevel: "delete" // Admin-level access
        }
    ],

    // 🔐 PERMISSIONS - Resolved from backend at runtime via moduleId "companyknowledge"
    permissions: {
        view: [],
        create: [],
        edit: [],
        delete: [],
        publish: []
    },

    // ⚡ QUICK ACTIONS
    quickActions: [
        {
            label: "New Article",
            path: "/companyknowledge/new",
            icon: "➕",
            description: "Create a new knowledge article"
        },
        {
            label: "View All Articles",
            path: "/companyknowledge",
            icon: "📚",
            description: "Browse all knowledge articles"
        },
        {
            label: "Admin Dashboard",
            path: "/companyknowledge/admin",
            icon: "⚙️",
            description: "Manage all articles and settings"
        }
    ],

    // 📋 CHANGELOG
    changelog: [
        {
            version: "0.0.8",
            releaseDate: "2026-04-29",
            title: "IPFS Standards Pass + Upload UX Fixes",
            features: [
                "🔴 Fix: videoIpfsUrl FieldResolver no longer crashes when parent query omits tenantId",
                "✅ tenantId added to GET_ARTICLE / GET_ARTICLE_FOR_EDIT / GET_PUBLIC_ARTICLE / GET_SHARED_ARTICLE queries",
                "✅ Friendly error boundaries — viewers no longer surface raw GraphQL stack traces",
                "✅ Phase 3 (IPFS hashing) now shows indeterminate spinner + elapsed seconds + 'this takes 3-4 min' explanation",
                "✅ Progress polling backs off to 5s during IPFS_PROCESSING (was 1s spam)",
                "✅ Module aligns with vault/how-to/ipfs-gateway-pattern.md hash-only standard"
            ],
            notes: "Triggered by Tom's 266MB video upload on 2026-04-29 surfacing a tenantId crash post-save. Module entity is already hash-only via videoIpfsHash; FieldResolver builds gateway URL at read time via buildTenantIpfsUrl. PLCC is the leading repo for companyknowledge — Module Upgrade Matrix will fan out to other tenant FEs."
        },
        {
            version: "0.0.7",
            releaseDate: "2025-11-25",
            title: "Short URLs & Unified Access Control",
            features: [
                "✅ YouTube-style short codes (10 chars) for all new articles",
                "✅ Unified /companyknowledge/{id} and /ck/{id} URL system",
                "✅ Visibility-based access control without mandatory auth",
                "✅ PUBLIC articles accessible without login",
                "✅ Clear GraphQL error codes (LOGIN_REQUIRED, INSUFFICIENT_PERMISSIONS, etc.)",
                "✅ User-friendly error messages with actionable feedback",
                "✅ Edit/Delete buttons hidden for non-authenticated public viewers",
                "✅ Removed old URL displays (/kb/shared, /kb/slug) from viewer",
                "✅ Backward compatible - old ObjectID URLs still work",
                "✅ Auto-generates shortCode using nanoid (collision-resistant)",
                "✅ Resolver accepts both ObjectID (24 chars) and shortCode (10 chars)",
                "✅ Copy link buttons use shortCode when available"
            ],
            notes: "Major UX improvement with 60% shorter URLs for new articles. Implemented unified access control system that allows PUBLIC articles to be viewed without authentication while maintaining security for restricted content. All existing ObjectID URLs continue working - zero breaking changes. Short codes are automatically generated for new articles using industry-standard nanoid library."
        },
        {
            version: "0.0.6",
            releaseDate: "2025-11-18",
            title: "Visibility System Refactoring - Schema Upgrade",
            features: [
                "✅ Split LOGIN_REQUIRED into ALL_LOGGED_IN and RESTRICTED for clarity",
                "✅ ALL_LOGGED_IN: Articles available to all authenticated users (no access control)",
                "✅ RESTRICTED: Articles with access control via tags or client IDs",
                "✅ Database migration: Successfully migrated 19 articles (8 → ALL_LOGGED_IN, 11 → RESTRICTED)",
                "✅ Updated visibility dropdown with clearer options",
                "✅ Enhanced badges: Cyan for ALL_LOGGED_IN, Blue for RESTRICTED",
                "✅ Backward compatibility maintained for old LOGIN_REQUIRED values",
                "✅ Updated all viewers (article, shared, public) to show correct visibility",
                "✅ GraphQL types regenerated for type safety"
            ],
            notes: "Major schema refactoring to make visibility levels crystal clear. The ambiguous LOGIN_REQUIRED has been replaced with two distinct options: ALL_LOGGED_IN (unrestricted access for any logged-in user) and RESTRICTED (access control via tags/client IDs). All existing articles were automatically migrated based on whether they had access control settings."
        },
        {
            version: "0.0.5",
            releaseDate: "2025-09-12",
            title: "Enhanced Visibility Filtering",
            features: [
                "✅ Added 'RESTRICTED ACCESS' filter to show only articles with access control",
                "✅ Renamed filter from 'LOGIN REQUIRED' to 'RESTRICTED ACCESS' for clarity",
                "✅ Added 'ALL LOGGED-IN USERS' filter for unrestricted login-required articles",
                "✅ Clear distinction between restricted vs unrestricted login-required content",
                "✅ Logical filtering hierarchy: first by restriction type, then by specific tags",
                "✅ Improved tooltips explaining each filter option"
            ],
            notes: "The filtering system now clearly separates articles with access control restrictions from those available to all logged-in users. 'RESTRICTED ACCESS' shows only articles with client tags or IDs set, while 'ALL LOGGED-IN USERS' shows articles available to everyone who's logged in. This makes it much easier to find and manage different types of content."
        },
        {
            version: "0.0.4",
            releaseDate: "2024-10-20",
            title: "Article Preview Control & Visibility Rename",
            features: [
                "✅ Added 'Show Article Preview' toggle for LOGIN_REQUIRED articles",
                "✅ Control whether shared links show article content or just login modal",
                "✅ Perfect for balancing transparency vs. security",
                "✅ Renamed 'SHARED' visibility to 'LOGIN_REQUIRED' for clarity",
                "✅ All 18 existing shared articles migrated automatically",
                "✅ Tooltips explain each visibility option clearly"
            ],
            notes: "You can now choose whether people see article previews on shared links. Enable preview for training materials you want to show, disable for sensitive documents. The visibility option 'SHARED' is now called 'LOGIN_REQUIRED' to better reflect what it does."
        },
        {
            version: "0.0.3",
            releaseDate: "2024-07-05",
            title: "Enhanced Access Control Visibility & UX",
            features: [
                "✅ Article list now displays access control information on each card",
                "✅ Shows required client tags with orange badges and client counts",
                "✅ Shows specific client names with purple badges",
                "✅ Hover tooltips reveal all clients who have access via tags",
                "✅ Separated article tags from access control in editor with distinct cards",
                "✅ Access control section has prominent orange border and lock icon for clarity",
                "✅ Clear visual distinction between organizational tags vs. access restrictions",
                "✅ Makes it crystal clear who has access to each article at a glance"
            ],
            notes: "Major UX improvement making access control transparent and easy to understand. Now you can see exactly who has access to articles both in the list view and editor, with helpful tooltips showing client names."
        },
        {
            version: "0.0.2",
            releaseDate: "2024-03-18",
            title: "Mermaid Diagram Support",
            features: [
                "✅ Added Mermaid.js diagram rendering in markdown articles",
                "✅ Support for flowcharts, sequence diagrams, class diagrams, and more",
                "✅ Automatic diagram rendering from ```mermaid code blocks",
                "✅ Works across all article viewers (private, public, and shared)",
                "✅ Upgraded mermaid package to v11.12.1"
            ],
            notes: "Articles can now include beautiful diagrams using Mermaid.js syntax. Simply use ```mermaid code blocks in your markdown content to create flowcharts, sequence diagrams, gantt charts, and many other diagram types."
        },
        {
            version: "0.0.1",
            releaseDate: "2023-08-22",
            title: "Initial Release - Production Ready Knowledge Base",
            features: [
                "✅ Article creation and editing with dual-mode support (Markdown & HTML)",
                "✅ Rich WYSIWYG editor with formatting tools (bold, italic, underline, lists, links, alignment)",
                "✅ Markdown support with import/export functionality",
                "✅ Video upload and embedding via IPFS",
                "✅ Three-tier visibility system (Private, Shared, Public)",
                "✅ Client-specific access control with tag-based permissions",
                "✅ Public URL slugs for shareable articles",
                "✅ Tag-based article organization and filtering",
                "✅ Draft mode for unpublished articles",
                "✅ Article pinning for important content",
                "✅ Meta description and SEO support",
                "✅ Image embedding support",
                "✅ File import (.md and .html)",
                "✅ Copy and download markdown templates",
                "✅ Admin dashboard for content management",
                "✅ Article search and filtering",
                "✅ Preview mode before publishing"
            ],
            notes: "First production-ready release of Company Knowledge module. Provides a complete internal knowledge base solution with flexible visibility controls, rich content editing, and advanced access management."
        }
    ],

    // 🗺️ ROADMAP
    roadmap: [
        {
            title: "Remove Backward Compatibility Code",
            description: "Clean up legacy LOGIN_REQUIRED compatibility layer after successful migration",
            targetVersion: "0.0.7",
            priority: "low",
            status: "planned",
            features: [
                "Remove LOGIN_REQUIRED backward compatibility checks from CompanyKnowledgeList.tsx",
                "Remove LOGIN_REQUIRED backward compatibility from CompanyKnowledgeViewer.tsx",
                "Remove SHARED enum value compatibility from all components",
                "Clean up migration scripts (archive for reference)",
                "Update documentation to reflect current schema only"
            ]
        },
        {
            title: "Enhanced Search & Discovery",
            description: "Advanced search capabilities with full-text search, filters, and AI-powered recommendations",
            targetVersion: "0.1.0",
            priority: "high",
            status: "planned",
            features: [
                "Full-text search across all article content",
                "Advanced filtering by category, tags, author, and date",
                "AI-powered related articles suggestions",
                "Recently viewed articles history",
                "Bookmarking and favorites system"
            ]
        },
        {
            title: "Article Templates & AI",
            description: "Pre-built templates and AI assistance for faster article creation",
            targetVersion: "0.2.0",
            priority: "high",
            status: "planned",
            features: [
                "Pre-built article templates (SOP, FAQ, Tutorial, Policy)",
                "AI writing assistant for content generation",
                "Auto-generated table of contents",
                "Article outline generator",
                "Smart content suggestions based on existing articles"
            ]
        },
        {
            title: "Collaboration Features",
            description: "Team collaboration tools for co-authoring and review workflows",
            targetVersion: "0.3.0",
            priority: "medium",
            status: "planned",
            features: [
                "Multi-author support with contributor tracking",
                "Comment system for article discussions",
                "Review and approval workflows",
                "Version history and rollback",
                "Real-time collaborative editing"
            ]
        },
        {
            title: "Analytics & Insights",
            description: "Track article performance and user engagement",
            targetVersion: "0.4.0",
            priority: "medium",
            status: "planned",
            features: [
                "Article view counts and analytics",
                "User engagement metrics",
                "Popular articles dashboard",
                "Search analytics and trending topics",
                "Knowledge gap identification"
            ]
        },
        {
            title: "Advanced Content Types",
            description: "Support for additional content formats and media types",
            targetVersion: "0.5.0",
            priority: "low",
            status: "planned",
            features: [
                "PDF document uploads and viewing",
                "Presentation mode for articles",
                "Interactive diagrams and flowcharts",
                "Audio file embedding",
                "Quiz and assessment creation"
            ]
        }
    ]
};

export default companyKnowledgeModuleConfig;
